if (customElements && !customElements.get('timer-countdown')) {
  /**
   * <timer-countdown> — countdown timer to a target date.
   *
   * Reads the target date from the data-time attribute and updates the text
   * of the nested elements with data-role="day|hour|min|sec" once per second.
   * Self-initializes when inserted into the DOM (connectedCallback) and
   * clears the interval when removed (disconnectedCallback).
   *
   * Attributes:
   *   data-time — target date in ISO 8601 format, e.g. "2026-09-05T04:00:00Z".
   *
   * Required markup (child elements):
   *   [data-role="day"]  — container for days
   *   [data-role="hour"] — container for hours
   *   [data-role="min"]  — container for minutes
   *   [data-role="sec"]  — container for seconds
   *
   * Events:
   *   countdown:end — bubbling event fired the moment the countdown reaches zero.
   *
   * Example:
   *   <timer-countdown data-time="2026-09-05T04:00:00Z">
   *     <span data-role="day">00</span>
   *     <span data-role="hour">00</span>
   *     <span data-role="min">00</span>
   *     <span data-role="sec">00</span>
   *   </timer-countdown>
   */

  class TimerCountdown extends HTMLElement {
    connectedCallback() {
      this.rawTime = this.dataset.time;
      if (!this.rawTime) {
        console.warn('Not found: countdown [data-time]');
        return;
      }

      this.targetTime = new Date(this.rawTime).getTime();

      if (Number.isNaN(this.targetTime)) {
        console.warn(`Invalid date: countdown [data-time] "${this.rawTime}"`);
        return;
      }

      this.elements = {
        day: this.querySelector('[data-role="day"]'),
        hour: this.querySelector('[data-role="hour"]'),
        min: this.querySelector('[data-role="min"]'),
        sec: this.querySelector('[data-role="sec"]')
      };

      this.init();
    }

    disconnectedCallback() {
      this.stop();
    }

    tick() {
      const diff = this.targetTime - Date.now();

      if (diff <= 0) {
        this.render(0, 0, 0, 0);
        this.stop();
        this.dispatchEvent(new CustomEvent('countdown:end', { bubbles: true }));
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);

      this.render(
        Math.floor(totalSeconds / 86400),
        Math.floor((totalSeconds % 86400) / 3600),
        Math.floor((totalSeconds % 3600) / 60),
        totalSeconds % 60
      );
    }

    render(days, hours, minutes, seconds) {
      this.elements.day.textContent = this.pad(days);
      this.elements.hour.textContent = this.pad(hours);
      this.elements.min.textContent = this.pad(minutes);
      this.elements.sec.textContent = this.pad(seconds);
    }

    pad(value) {
      return String(value).padStart(2, '0');
    }

    stop() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }

    init() {
      const missing = Object.entries(this.elements).find(([key, value]) => !value);

      if (missing) {
        console.warn(`Not found: countdown element [data-role="${missing[0]}"]`);
        return;
      }

      this.tick();
      this.timerId = setInterval(() => this.tick(), 1000);
    };
  }

  customElements.define('timer-countdown', TimerCountdown);
}